import { useEffect, useRef, useState } from 'react'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'

import { generateCoinbaseSessionToken } from './generateCoinbaseSessionToken'

export function useAutoGenerateCoinbaseSessionToken(options?: { retryBackoffMs?: number }) {
    const { data: myAbstractAccountAddress } = useMyAbstractAccountAddress()
    const retryBackoffMs = options?.retryBackoffMs ?? 5_000
    const prevAddressRef = useRef<string | undefined>(undefined)
    const [sessionToken, setSessionToken] = useState<{
        token: string | null
        createdAt: number
    }>({
        token: null,

        createdAt: 0,
    })

    useEffect(() => {
        if (!myAbstractAccountAddress) {
            return
        }

        // Clear stale token when address changes
        if (prevAddressRef.current !== myAbstractAccountAddress) {
            setSessionToken({ token: null, createdAt: 0 })
        }

        prevAddressRef.current = myAbstractAccountAddress

        let isMounted = true
        let refreshTimer: ReturnType<typeof setTimeout>

        const generateAndScheduleRefresh = async () => {
            try {
                const token = await generateCoinbaseSessionToken(myAbstractAccountAddress)
                if (isMounted) {
                    setSessionToken({ token, createdAt: Date.now() })
                    // Coinbase session tokens are valid for 5 minutes
                    // Regenerate token after 4 minutes (240 seconds)
                    refreshTimer = setTimeout(
                        () => {
                            generateAndScheduleRefresh()
                        },
                        4 * 60 * 1000,
                    )
                }
            } catch (error) {
                console.error('[CoinbaseOnramp] Failed to generate session token:', error)
                // Schedule retry with backoff on failure
                if (isMounted) {
                    refreshTimer = setTimeout(() => {
                        generateAndScheduleRefresh()
                    }, retryBackoffMs)
                }
            }
        }

        generateAndScheduleRefresh()

        return () => {
            isMounted = false
            if (refreshTimer) {
                clearTimeout(refreshTimer)
            }
        }
    }, [myAbstractAccountAddress, retryBackoffMs])

    return sessionToken
}
